import {
  ComputedNavItem,
  VenuePageUrlBase,
  computeNavItems,
} from "../client/components/navigation/utils";
import { Database, Tables } from "@punchup/data/supabase.types";
import { type BaseNavItemKey } from "./navigation";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  VenuePageCustomLink,
  VenuePageExternalLink,
  VenuePageNavItemRow,
  VenuePagesRow,
} from "./venue_pages";
import {
  VenuePageNavGroupRow,
  getVenuePageNavGroups,
} from "./venue_pages_nav_groups";

import { SupabaseClient } from "@supabase/supabase-js";

/**
 * Sentinel position for hidden nav items. Real positions are >= 0; -1 keeps
 * hidden rows out of the shown-list ordering and sorts them below it for any
 * code that does a global sort.
 */
const HIDDEN_POSITION = -1;

/**
 * Returns the next available position for a newly-shown nav item in a
 * container: one greater than the max non-hidden position, or 0 if empty.
 * `parentGroupId` scopes the container (null = top level, a group id = that
 * group's children); omit it for top-level placement.
 */
async function getNextShownPosition(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  parentGroupId: string | null = null
): Promise<number> {
  const baseQuery = supabase
    .from("venue_pages_nav_items")
    .select("position")
    .eq("venue_page_id", venuePageId)
    .eq("is_hidden", false);
  const scopedQuery =
    parentGroupId === null
      ? baseQuery.is("parent_group_id", null)
      : baseQuery.eq("parent_group_id", parentGroupId);

  const { data, error } = await scopedQuery
    .order("position", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logException(error, {
      extra: { data: "getNextShownPosition", venuePageId, parentGroupId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data ? data.position + 1 : 0;
}

/**
 * Data model - visibility vs. content for built-in pages:
 *
 * `venue_pages_nav_items` controls navbar visibility. For non-built-in base
 * items (Home, Calendar, Menu, etc.) the absence of a row means "show by
 * default"; an `is_hidden=true` row hides them. For built-in pages
 * (Free Shows, Shows Tonight) the absence of a row means "do not show"
 * (opt-in), and an `is_hidden=false` row surfaces them.
 *
 * `venue_pages_custom_links` is the storage for user-authored custom pages.
 * A row whose `path` matches a built-in key ('free-shows' / 'shows-tonight')
 * is treated as a content override for that built-in page (title/body) - it
 * does NOT control nav visibility on its own. Deleting a built-in's
 * custom_links row resets its content to the generated defaults but does
 * not change whether it appears in the nav.
 */

/**
 * Fetches nav item overrides from the database.
 * Returns empty array if no overrides exist (most venues).
 */
export async function getVenuePageNavItems(
  supabase: SupabaseClient<Database>,
  venuePageId: string
): Promise<VenuePageNavItemRow[]> {
  try {
    const { data } = await supabase
      .from("venue_pages_nav_items")
      .select("*")
      .eq("venue_page_id", venuePageId)
      .order("position", { ascending: true })
      .throwOnError();

    return data ?? [];
  } catch (error) {
    logException(error, {
      extra: { data: "getVenuePageNavItems", venuePageId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }
}

/**
 * Sets the visibility for a base nav item by upserting into
 * venue_pages_nav_items. Used by the built-in pages settings UI to
 * hide/show items like Home, Calendar, Map & Parking, etc.
 *
 * Hidden rows are written with `position = -1`. Newly-shown rows are appended
 * to the end of the shown list (`max(position) + 1`), so the user can drag
 * them wherever they want from there. See the plan doc at
 * docs/plans/venue-pages-nav-hidden-position.md for the full rationale.
 */
export async function setBaseNavItemHidden({
  supabase,
  venuePageId,
  itemKey,
  isHidden,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  itemKey: BaseNavItemKey;
  isHidden: boolean;
}): Promise<VenuePageNavItemRow> {
  const position = isHidden
    ? HIDDEN_POSITION
    : await getNextShownPosition(supabase, venuePageId);

  const { data, error } = await supabase
    .from("venue_pages_nav_items")
    .upsert(
      {
        venue_page_id: venuePageId,
        item_type: "base",
        item_key: itemKey,
        is_hidden: isHidden,
        position,
      },
      { onConflict: "venue_page_id,item_type,item_key" }
    )
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "setBaseNavItemHidden", venuePageId, itemKey },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

/**
 * Sets the visibility for a custom-link nav item by upserting into
 * venue_pages_nav_items. Lets the page exist at its URL while being
 * hidden from the navbar.
 *
 * Hide writes `position = -1`; show appends to the end of the shown list.
 */
export async function setCustomNavItemHidden({
  supabase,
  venuePageId,
  customLinkId,
  isHidden,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  customLinkId: number;
  isHidden: boolean;
}): Promise<VenuePageNavItemRow> {
  const position = isHidden
    ? HIDDEN_POSITION
    : await getNextShownPosition(supabase, venuePageId);

  const { data, error } = await supabase
    .from("venue_pages_nav_items")
    .upsert(
      {
        venue_page_id: venuePageId,
        item_type: "custom",
        item_key: String(customLinkId),
        is_hidden: isHidden,
        position,
      },
      { onConflict: "venue_page_id,item_type,item_key" }
    )
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "setCustomNavItemHidden", venuePageId, customLinkId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

/**
 * Sets the visibility for an external-link nav item by upserting into
 * venue_pages_nav_items. Hide writes `position = -1`; show appends to the
 * end of the shown list.
 */
export async function setExternalNavItemHidden({
  supabase,
  venuePageId,
  externalLinkId,
  isHidden,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  externalLinkId: number;
  isHidden: boolean;
}): Promise<VenuePageNavItemRow> {
  const position = isHidden
    ? HIDDEN_POSITION
    : await getNextShownPosition(supabase, venuePageId);

  const { data, error } = await supabase
    .from("venue_pages_nav_items")
    .upsert(
      {
        venue_page_id: venuePageId,
        item_type: "external",
        item_key: String(externalLinkId),
        is_hidden: isHidden,
        position,
      },
      { onConflict: "venue_page_id,item_type,item_key" }
    )
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "setExternalNavItemHidden", venuePageId, externalLinkId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

/**
 * Upserts a single nav item appended to the end of its destination container
 * (top level when `parentGroupId` is null, else that group's children).
 * `logContext` labels the Sentry breadcrumb for the calling operation.
 */
async function upsertNavItemAtEndOfContainer({
  supabase,
  venuePageId,
  itemType,
  itemKey,
  parentGroupId,
  logContext,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  itemType: VenuePageNavItemRow["item_type"];
  itemKey: string;
  parentGroupId: string | null;
  logContext: Record<string, unknown>;
}): Promise<VenuePageNavItemRow> {
  const position = await getNextShownPosition(
    supabase,
    venuePageId,
    parentGroupId
  );

  const { data, error } = await supabase
    .from("venue_pages_nav_items")
    .upsert(
      {
        venue_page_id: venuePageId,
        item_type: itemType,
        item_key: itemKey,
        parent_group_id: parentGroupId,
        is_hidden: false,
        position,
      },
      { onConflict: "venue_page_id,item_type,item_key" }
    )
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: logContext,
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

/**
 * Inserts a `nav_items` row for a newly-created dropdown group, appending it
 * to the end of the top-level shown list. The group's own label / id live on
 * `venue_pages_nav_groups`; this row is what places it in the list ordering.
 */
export async function appendGroupNavItem({
  supabase,
  venuePageId,
  groupId,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  groupId: string;
}): Promise<VenuePageNavItemRow> {
  return upsertNavItemAtEndOfContainer({
    supabase,
    venuePageId,
    itemType: "group",
    itemKey: groupId,
    parentGroupId: null,
    logContext: { data: "appendGroupNavItem", venuePageId, groupId },
  });
}

/**
 * Persists a new ordering for one container of a venue page's shown nav
 * items by upserting a row per entry with sequential `position` values
 * starting at 0. `parentGroupId` scopes the ordering: `null` for the
 * top-level list, a group id for the children of that group. Each entry
 * keeps its existing visibility (`is_hidden: false` for shown items being
 * reordered). Returns the saved rows so callers can merge them into local
 * state.
 */
export async function setVenuePageNavItemsOrder({
  supabase,
  venuePageId,
  parentGroupId = null,
  orderedItems,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  parentGroupId?: string | null;
  orderedItems: Array<{
    item_type: "base" | "custom" | "external" | "group";
    item_key: string;
  }>;
}): Promise<VenuePageNavItemRow[]> {
  if (orderedItems.length === 0) return [];

  const rows = orderedItems.map((item, index) => ({
    venue_page_id: venuePageId,
    item_type: item.item_type,
    item_key: item.item_key,
    parent_group_id: parentGroupId,
    is_hidden: false,
    position: index,
  }));

  const { data, error } = await supabase
    .from("venue_pages_nav_items")
    .upsert(rows, { onConflict: "venue_page_id,item_type,item_key" })
    .select();

  if (error) {
    logException(error, {
      extra: { data: "setVenuePageNavItemsOrder", venuePageId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data ?? [];
}

/**
 * Fetches and computes the final nav items for a venue page.
 * This is the main function to use server-side - it handles everything in one call.
 */
export async function getComputedVenuePageNavItems(
  supabase: SupabaseClient<Database>,
  params: {
    venuePage: VenuePagesRow;
    customLinks: VenuePageCustomLink[];
    externalLinks: VenuePageExternalLink[];
    hasFAQs: boolean;
    hasMenu: boolean;
    urlBase: VenuePageUrlBase;
  }
): Promise<ComputedNavItem[]> {
  const [navItems, navGroups] = await Promise.all([
    getVenuePageNavItems(supabase, params.venuePage.id),
    getVenuePageNavGroups(supabase, params.venuePage.id),
  ]);

  return computeNavItems({
    ...params,
    navItems,
    navGroups,
  });
}
